import React from "react";
import { Helmet } from "react-helmet";
import HeaderThree from "../../component/header/HeaderThree";
import FooterTwo from "../../component/footer/FooterTwo";
import { useTranslation } from "react-i18next";
import {
  pressPath,
  articlePath,
  pressArticles,
  pageChrome,
  aboutBoilerplate,
  pressAssets,
  pressContactEmail,
} from "./pressContent";

const siteUrl = "https://ecoshift.eco";
const supportedLangs = ["fr", "en", "nl", "de"];

const PressPage = () => {
  const { i18n } = useTranslation();
  const lang = supportedLangs.includes(i18n.language) ? i18n.language : "en";

  const chrome = pageChrome[lang];
  const [heroArticle, ...olderArticles] = pressArticles;

  const trackEvent = (category, action, name) => {
    if (window._paq) {
      window._paq.push(["trackEvent", category, action, name]);
    }
  };

  return (
    <div className="active-dark bg_color--ugs">
      <Helmet>
        <title>{chrome.pageTitle} — EcoShift</title>
        <meta name="description" content={chrome.metaDescription} />
        <meta property="og:title" content={`${chrome.pageTitle} — EcoShift`} />
        <meta property="og:description" content={chrome.metaDescription} />
        <meta property="og:url" content={`${siteUrl}${pressPath}`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${siteUrl}${pressPath}`} />
      </Helmet>

      <HeaderThree homeLink="/" logo="symbol-dark" color="color-black" alwaysSticky="true" />

      <div className="press-page">
        <div className="container press-page__inner">
          <p className="press-page__kicker">{chrome.kicker}</p>

          {/* Block A — featured article */}
          <section className="press-hero" aria-label={chrome.featuredLabel}>
            <div className="press-hero__source">
              <img
                src={heroArticle.sourceLogo}
                alt={heroArticle.sourceName}
                className="press-hero__source-logo"
              />
              <span className="press-hero__date">{heroArticle.dateLabel[lang]}</span>
            </div>

            <h1 className="press-hero__headline">{heroArticle.headline[lang]}</h1>
            <p className="press-hero__summary">{heroArticle.summary[lang]}</p>

            <blockquote className="press-hero__quote">
              <p>&ldquo;{heroArticle.pullQuote[lang]}&rdquo;</p>
              <cite>{heroArticle.pullQuoteAuthor[lang]}</cite>
            </blockquote>

            <p className="press-hero__credit">
              {chrome.creditPrefix} {heroArticle.credit[lang]}
            </p>

            <div className="press-hero__actions">
              <a
                className="btn-default-green custom"
                href={heroArticle.pdfHref}
                download
                onClick={() => trackEvent("Press", "PDF download", heroArticle.id)}
              >
                {chrome.readPdf}
              </a>
              <a
                className="btn-default custom"
                href={heroArticle.originalArticleUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {chrome.readOriginal}
              </a>
              {chrome.readTranslation && (
                <a
                  className="btn-default custom"
                  href={`${process.env.PUBLIC_URL}${articlePath}`}
                  onClick={() => trackEvent("Press", "Translation view", heroArticle.id)}
                >
                  {chrome.readTranslation}
                </a>
              )}
            </div>
          </section>

          {olderArticles.length > 0 && (
            <section className="press-older" aria-label={chrome.olderLabel}>
              <p className="press-older__title">{chrome.olderLabel}</p>
              <ul className="press-older__list">
                {olderArticles.map((article) => (
                  <li key={article.id} className="press-older__row">
                    <img src={article.sourceLogo} alt={article.sourceName} />
                    <span className="press-older__date">{article.dateLabel[lang]}</span>
                    <a href={article.pdfHref} className="press-older__link">
                      {article.headline[lang]}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Block B — press kit */}
          <section className="press-kit">
            <p className="press-kit__title">{chrome.pressKitTitle}</p>
            <p className="press-kit__intro">{chrome.pressKitIntro}</p>

            <div className="press-kit__about">
              <p className="press-kit__about-title">{chrome.aboutTitle}</p>
              <p className="press-kit__about-text">{aboutBoilerplate[lang]}</p>
            </div>

            <div className="press-kit__downloads">
              <div className="press-kit__item">
                <img src={pressAssets.ecoshiftLogo} alt="EcoShift logo" />
                <a
                  className="btn-default custom"
                  href={pressAssets.ecoshiftLogo}
                  download
                  onClick={() => trackEvent("Press", "Asset download", "logo")}
                >
                  {chrome.downloadLogo}
                </a>
              </div>
              <div className="press-kit__item">
                <img src={pressAssets.founderPhoto} alt="Baudouin Denis, co-founder EcoShift" />
                <a
                  className="btn-default custom"
                  href={pressAssets.founderPhoto}
                  download
                  onClick={() => trackEvent("Press", "Asset download", "founder-photo")}
                >
                  {chrome.downloadPhoto}
                </a>
              </div>
            </div>

            <p className="press-kit__contact">
              {chrome.pressContactLabel}: <a href={`mailto:${pressContactEmail}`}>{pressContactEmail}</a>
            </p>
          </section>
        </div>
      </div>

      <FooterTwo homeLink="/" />
    </div>
  );
};

export default PressPage;
